import moment from 'moment';

export const NOT_CALCULATED_LABEL = 'Not Calculated';

const normalizeEventLabel = (label?: string) =>
  String(label ?? '')
    .trim()
    .toLowerCase();

export const getLatestEventByLabels = (
  events: any[] = [],
  labels: Array<string | undefined>,
) => {
  const normalizedLabels = labels
    .map((label) => normalizeEventLabel(label))
    .filter(Boolean);

  return [...events]
    .filter((episode: any) => {
      const description = normalizeEventLabel(episode?.detail?.description);
      const name = normalizeEventLabel(episode?.name);
      return (
        normalizedLabels.includes(description) ||
        normalizedLabels.includes(name)
      );
    })
    .sort(
      (a: any, b: any) =>
        new Date(b?.event_date ?? 0).getTime() -
        new Date(a?.event_date ?? 0).getTime(),
    )[0];
};

const toNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === 'string') {
    const cleaned = value.replace(/[^0-9.-]/g, '');
    const parsed = Number(cleaned);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
};

const toWeightKg = (value: unknown, unit?: string): number | null => {
  const numericValue = toNumber(value);
  if (numericValue == null) return null;

  const normalizedUnit = String(unit ?? '')
    .trim()
    .toLowerCase();

  if (normalizedUnit.includes('lb')) {
    return numericValue * 0.45359237;
  }

  return numericValue;
};

const toHeightMeters = (value: unknown, unit?: string): number | null => {
  const numericValue = toNumber(value);
  if (numericValue == null) return null;

  const normalizedUnit = String(unit ?? '')
    .trim()
    .toLowerCase();

  if (normalizedUnit.includes('cm')) {
    return numericValue / 100;
  }
  if (
    normalizedUnit === 'm' ||
    normalizedUnit.includes('meter') ||
    normalizedUnit.includes('metre')
  ) {
    return numericValue;
  }
  if (normalizedUnit.includes('in')) {
    return (numericValue * 2.54) / 100;
  }

  return numericValue > 10 ? numericValue / 100 : numericValue;
};

const formatCalculatedMetric = (value: number | null, unit: string) => {
  if (value == null || !Number.isFinite(value)) {
    return NOT_CALCULATED_LABEL;
  }
  return `${value.toFixed(1)}${unit} `;
};

export const getLatestHeightWeight = (events: any[] = []) => {
  const heightEvent = getLatestEventByLabels(events, ['Height', 'height']);
  const weightEvent = getLatestEventByLabels(events, ['Weight', 'weight']);

  const heightValue = heightEvent?.detail?.fhir?.valueQuantity?.value;
  const heightUnit = heightEvent?.detail?.fhir?.valueQuantity?.unit;
  const weightValue = weightEvent?.detail?.fhir?.valueQuantity?.value;
  const weightUnit = weightEvent?.detail?.fhir?.valueQuantity?.unit;

  const heightMeters = toHeightMeters(heightValue, heightUnit);
  const weightKg = toWeightKg(weightValue, weightUnit);

  const dateStr =
    heightEvent?.event_date || weightEvent?.event_date
      ? moment(
          new Date(
            Math.max(
              new Date(heightEvent?.event_date ?? 0).getTime(),
              new Date(weightEvent?.event_date ?? 0).getTime(),
            ),
          ),
        ).format('DD MMM YY')
      : '';

  return {
    heightEvent,
    weightEvent,
    heightMeters,
    heightCm: heightMeters == null ? null : heightMeters * 100,
    weightKg,
    dateStr,
  };
};

export const getBmiDisplayFromEvents = (events: any[] = []) => {
  const { heightMeters, weightKg, dateStr } = getLatestHeightWeight(events);
  if (heightMeters == null || weightKg == null) {
    return { displayValue: NOT_CALCULATED_LABEL, dateStr };
  }

  const bmiValue = weightKg / (heightMeters * heightMeters);
  return { displayValue: formatCalculatedMetric(bmiValue, ''), dateStr };
};
